import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { websiteApi } from "../../../api/websiteApi";
import { paymentApi } from "../../../api/paymentApi";
import { useNotifications } from "../../../context/NotificationContext";
import { toast } from "react-toastify";
import {
  DEFAULT_WEBSITE_ONBOARDING_QUESTIONS,
  parseWebsiteOnboardingQuestions,
} from "../../client/StoreRequest/websiteOnboardingQuestions";
import "../StoreRequests/RequestDetail.css";
import "./WebsiteRequests.css";

const parseObject = (value) => {
  if (!value) return {};
  if (typeof value === "object") return value;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const formatDate = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "N/A"
    : date.toLocaleString("en-ZA", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
};

const formatLabel = (value) =>
  String(value || "pending")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return "N/A";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "N/A";
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  return String(value);
};

const getDomainName = (domain) => {
  if (domain.domainType === "custom") {
    const name = String(domain.customDomain || "").trim();
    const extension = String(domain.selectedExtension || "").trim();
    if (!name) return extension || "N/A";
    return extension && !name.toLowerCase().endsWith(extension.toLowerCase())
      ? `${name}${extension}`
      : name;
  }
  if (domain.domainType === "existing") {
    return domain.existingDomain || "N/A";
  }
  if (domain.domainType === "subdomain") {
    return domain.subdomain
      ? `${domain.subdomain}.tooraflex.co.za`
      : "N/A";
  }
  return "N/A";
};

const getStatusBadgeClass = (status) => {
  const statusClasses = {
    pending: "status-pending",
    approved: "status-approved",
    rejected: "status-rejected",
    in_progress: "status-progress",
    ready_for_review: "status-approved",
    live: "status-live",
    suspended: "status-suspended",
  };
  return statusClasses[status] || "status-pending";
};

const findWebsiteAnswer = (answers, questions, idPattern, labelPattern) => {
  for (const section of questions) {
    for (const question of section.questions) {
      if (
        (idPattern.test(question.id) || labelPattern.test(question.label)) &&
        answers[question.id] !== undefined &&
        answers[question.id] !== null &&
        answers[question.id] !== ""
      ) {
        return {
          answer: answers[question.id],
          label: question.label,
        };
      }
    }
  }
  return null;
};

const getWebsiteReadiness = (answers, questions) => {
  const criteria = [
    {
      id: /websitepurpose|purpose/i,
      label: /main purpose|purpose of your website/i,
      weight: 20,
      score: () => 100,
    },
    {
      id: /websitetype|sitetype/i,
      label: /kind of website|type of website/i,
      weight: 15,
      score: () => 100,
    },
    {
      id: /requiredpages|pages|features/i,
      label: /which pages|pages or features/i,
      weight: 15,
      score: () => 100,
    },
    {
      id: /contentreadiness|content.*ready/i,
      label: /how ready.*content|text, images|brand assets/i,
      weight: 35,
      score: (answer) => {
        const value = String(answer).toLowerCase();
        if (/some/.test(value)) return 65;
        if (/help/.test(value)) return 45;
        if (/not ready/.test(value)) return 25;
        if (/ready/.test(value)) return 100;
        return null;
      },
    },
    {
      id: /targetlaunchdate|launchdate/i,
      label: /when.*website.*launch|launch date/i,
      weight: 15,
      score: (answer) => {
        const targetDate = new Date(answer);
        if (Number.isNaN(targetDate.getTime())) return null;
        const daysUntilLaunch = Math.ceil(
          (targetDate.setHours(0, 0, 0, 0) -
            new Date().setHours(0, 0, 0, 0)) /
            (24 * 60 * 60 * 1000),
        );
        if (daysUntilLaunch < 0) return 0;
        if (daysUntilLaunch <= 30) return 100;
        if (daysUntilLaunch <= 90) return 80;
        return 60;
      },
    },
  ];
  const scored = [];

  for (const criterion of criteria) {
    const entry = findWebsiteAnswer(
      answers,
      questions,
      criterion.id,
      criterion.label,
    );
    if (!entry) continue;
    const score = criterion.score(entry.answer);
    if (score !== null) scored.push({ weight: criterion.weight, score });
  }

  const availableWeight = scored.reduce((total, item) => total + item.weight, 0);
  const score =
    availableWeight > 0
      ? Math.round(
          scored.reduce(
            (total, item) => total + item.score * item.weight,
            0,
          ) / availableWeight,
        )
      : null;
  const coverage = Math.round((availableWeight / 100) * 100);
  const level =
    scored.length < 2
      ? "Not enough data"
      : score >= 75
        ? "Ready to launch"
        : score >= 45
          ? "Preparing"
          : "Planning";
  const supportEntry = findWebsiteAnswer(
    answers,
    questions,
    /websitesupportareas|supportareas|support/i,
    /what would you like help with|support areas/i,
  );
  const supportValues = Array.isArray(supportEntry?.answer)
    ? supportEntry.answer
    : supportEntry?.answer
      ? [supportEntry.answer]
      : [];
  const contentEntry = findWebsiteAnswer(
    answers,
    questions,
    /contentreadiness|content.*ready/i,
    /how ready.*content|text, images|brand assets/i,
  );
  const contentNeedsHelp = /not ready|need help|some/i.test(
    String(contentEntry?.answer || ""),
  );
  const supportFactors = [];
  if (supportValues.length) supportFactors.push("Requested project assistance");
  if (contentNeedsHelp) supportFactors.push("Content and brand assets need preparation");
  const supportLevel =
    supportFactors.length > 1
      ? "High-touch guidance"
      : supportFactors.length
        ? "Guided support"
        : supportEntry || contentEntry
          ? "Self-service likely"
          : null;
  const recommendations = [];

  if (
    !findWebsiteAnswer(
      answers,
      questions,
      /requiredpages|pages|features/i,
      /which pages|pages or features/i,
    )
  ) {
    recommendations.push("Confirm the pages and features required for the website.");
  }
  if (contentNeedsHelp) {
    recommendations.push("Agree on a plan for preparing website copy, images and brand assets.");
  }
  if (supportValues.length) {
    recommendations.push(`Follow up on requested support: ${supportValues.join(", ")}.`);
  }

  return {
    score,
    level,
    coverage,
    supportLevel,
    supportFactors,
    recommendations,
  };
};

const getStepCompletion = (stepId, request, domain, invoice) => {
  switch (stepId) {
    case "package":
      return Boolean(request.WebsitePackageID && request.PackageName);
    case "business":
      return Boolean(
        request.BusinessName && request.ContactName && request.Email,
      );
    case "domain":
      return Boolean(domain.domainType);
    case "agreement":
      return Boolean(request.ContractAcceptedAt);
    case "invoice":
      return Boolean(request.InvoiceNumber && Object.keys(invoice).length);
    default:
      return false;
  }
};

const WebsiteRequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { socket } = useNotifications();
  const [request, setRequest] = useState(null);
  const [questionSections, setQuestionSections] = useState(
    DEFAULT_WEBSITE_ONBOARDING_QUESTIONS,
  );
  const [questionsError, setQuestionsError] = useState("");
  const [onboardingSteps, setOnboardingSteps] = useState([]);
  const [stepsError, setStepsError] = useState("");
  const [status, setStatus] = useState("pending");
  const [quote, setQuote] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmingPayment, setConfirmingPayment] = useState(false);
  const [error, setError] = useState("");

  const loadRequest = useCallback(async () => {
    setError("");
    try {
      const response = await websiteApi.getAdminRequest(id);
      const data = response.data;
      setRequest(data);
      setStatus(String(data.Status || "pending").toLowerCase());
      const invoice = parseObject(data.InvoiceData);
      setQuote(invoice.quoteRequired ? "" : invoice.totalDue ?? "");
    } catch (loadError) {
      setError(
        loadError.response?.data?.error ||
          "Could not load this website request.",
      );
      setRequest(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    loadRequest();
  }, [loadRequest]);

  useEffect(() => {
    let isCurrent = true;
    const loadConfiguration = async () => {
      const [questionsResult, stepsResult] = await Promise.allSettled([
        websiteApi.getOnboardingQuestions(),
        websiteApi.getOnboardingSteps(),
      ]);
      if (!isCurrent) return;

      if (questionsResult.status === "fulfilled") {
        try {
          setQuestionSections(
            parseWebsiteOnboardingQuestions(questionsResult.value.data),
          );
          setQuestionsError("");
        } catch (questionError) {
          console.error("Could not parse website onboarding questions:", questionError);
          setQuestionsError(
            "Could not load the configured question labels. Submitted answers are still shown below.",
          );
        }
      } else {
        console.error(
          "Could not load website onboarding questions:",
          questionsResult.reason,
        );
        setQuestionsError(
          "Could not load the configured question labels. Submitted answers are still shown below.",
        );
      }

      if (
        stepsResult.status === "fulfilled" &&
        Array.isArray(stepsResult.value.data)
      ) {
        setOnboardingSteps(stepsResult.value.data);
        setStepsError("");
      } else {
        const stepError =
          stepsResult.status === "rejected"
            ? stepsResult.reason
            : new Error("Website onboarding steps were returned in an invalid format.");
        console.error("Could not load website onboarding steps:", stepError);
        setStepsError(
          "Could not load configured website onboarding steps.",
        );
      }
    };
    loadConfiguration();
    return () => {
      isCurrent = false;
    };
  }, []);

  useEffect(() => {
    if (!socket) return undefined;
    const refresh = (payload) => {
      if (!payload?.websiteRequestId || payload.websiteRequestId === id) {
        loadRequest();
      }
    };
    socket.on("website-request:updated", refresh);
    return () => socket.off("website-request:updated", refresh);
  }, [id, loadRequest, socket]);

  const saveStatus = async (nextStatus) => {
    setSaving(true);
    setError("");
    try {
      await websiteApi.updateRequestStatus(id, nextStatus);
      toast.success(`Status updated to ${formatLabel(nextStatus)}.`);
      await loadRequest();
    } catch (saveError) {
      setError(
        saveError.response?.data?.error ||
          "Could not update request status.",
      );
    } finally {
      setSaving(false);
    }
  };

  const confirmPayment = async () => {
    if (!request?.LatestPaymentID) return;
    const transactionId = window.prompt(
      "Optional: enter the transaction/reference ID after verifying receipt.",
    );
    if (transactionId === null) return;

    setConfirmingPayment(true);
    setError("");
    try {
      await paymentApi.confirmPayment(
        request.LatestPaymentID,
        transactionId.trim(),
      );
      toast.success("Payment confirmed and request approved.");
      await loadRequest();
    } catch (confirmError) {
      setError(
        confirmError.response?.data?.error || "Failed to confirm payment.",
      );
    } finally {
      setConfirmingPayment(false);
    }
  };

  const saveQuote = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      await websiteApi.setRequestQuote(id, Number(quote));
      toast.success("Quote saved and invoice updated.");
      await loadRequest();
    } catch (saveError) {
      setError(
        saveError.response?.data?.error ||
          "Could not save the website quote.",
      );
    } finally {
      setSaving(false);
    }
  };

  const printDocument = (documentType) => {
    const printClass = `printing-admin-${documentType}`;
    const clearPrintClass = () => {
      document.body.classList.remove(printClass);
      window.removeEventListener("afterprint", clearPrintClass);
    };
    document.body.classList.remove(
      "printing-admin-invoice",
      "printing-admin-contract",
    );
    document.body.classList.add(printClass);
    window.addEventListener("afterprint", clearPrintClass);
    window.print();
  };

  if (loading) {
    return (
      <div className="request-detail-loading">
        <div className="spinner" />
        <p>Loading website request details...</p>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="request-detail-not-found">
        <div className="not-found-icon">🔍</div>
        <h3>Website Request Not Found</h3>
        <p>{error || "The website request could not be found."}</p>
        <button
          className="btn-primary"
          onClick={() => navigate("/admin/website-requests")}
        >
          Back to Website Requests
        </button>
      </div>
    );
  }

  const domain = parseObject(request.DomainData);
  const invoice = parseObject(request.InvoiceData);
  const answers = parseObject(request.Answers);
  const currency = invoice.currency || request.LatestPaymentCurrency || "ZAR";
  const selectedDomain = getDomainName(domain);
  const amount = invoice.totalDue ?? request.LatestPaymentAmount;
  const totalDue =
    amount === null || amount === undefined
      ? "Custom quote required"
      : `${currency} ${Number(amount).toFixed(2)}`;
  const isQuoteRequired = Boolean(
    invoice.quoteRequired ?? request.IsQuoteOnly,
  );
  const paymentDetails = parseObject(request.LatestPaymentData);
  const configuredQuestions = questionSections.flatMap((section) =>
    section.questions.map((question) => ({
      ...question,
      sectionTitle: section.title,
    })),
  );
  const submittedQuestionIds = new Set(
    configuredQuestions.map((question) => question.id),
  );
  const unconfiguredAnswers = Object.entries(answers).filter(
    ([questionId, answer]) =>
      !submittedQuestionIds.has(questionId) &&
      answer !== undefined &&
      answer !== null &&
      answer !== "" &&
      !(Array.isArray(answer) && !answer.length),
  );
  const readiness = getWebsiteReadiness(answers, questionSections);
  const answeredQuestions = configuredQuestions.filter(
    (question) =>
      answers[question.id] !== undefined &&
      answers[question.id] !== null &&
      answers[question.id] !== "" &&
      !(Array.isArray(answers[question.id]) && !answers[question.id].length),
  );
  const progressSteps = onboardingSteps
    .filter((step) => step.enabled !== false)
    .sort((first, second) => Number(first.order || 0) - Number(second.order || 0))
    .map((step, index) => ({
      ...step,
      stepNumber: index + 1,
      completed: getStepCompletion(step.id, request, domain, invoice),
    }));
  const completedSteps = progressSteps.filter((step) => step.completed).length;
  const progressPercent = progressSteps.length
    ? Math.round((completedSteps / progressSteps.length) * 100)
    : 0;

  return (
    <div className="request-detail-page website-request-manage-page">
      <header className="request-detail-header">
        <div className="header-left">
          <button
            className="back-btn"
            type="button"
            onClick={() => navigate("/admin/website-requests")}
          >
            <i className="fas fa-arrow-left" /> Back
          </button>
          <div className="header-info">
            <h1>{request.BusinessName || "Website request"}</h1>
            <div className="header-meta">
              <span className="request-number">
                #{request.InvoiceNumber || request.WebsiteRequestID}
              </span>
              <span className="header-divider">•</span>
              <span className="package-name">
                {request.PackageName || "Website package"}
              </span>
              <span className="header-divider">•</span>
              <span className="submitted-date">
                Submitted {formatDate(request.CreatedAt)}
              </span>
            </div>
          </div>
        </div>
        <div className="header-right">
          <a className="communication-jump-link" href="#website-request-invoice">
            <i className="fas fa-file-invoice-dollar" /> Invoice
          </a>
          <span
            className={`status-badge ${getStatusBadgeClass(status)}`}
          >
            {formatLabel(status)}
          </span>
        </div>
      </header>

      {error && (
        <div className="alert alert-error" role="alert">
          <i className="fas fa-exclamation-circle" />
          {error}
        </div>
      )}
      {questionsError && (
        <div className="alert alert-error" role="alert">
          <i className="fas fa-exclamation-circle" />
          {questionsError}
        </div>
      )}

      <div className="request-detail-grid">
        <div className="detail-left-column">
          <section className="detail-card status-actions-card">
            <h3>Status Actions</h3>
            <div className="status-actions">
              <div className="status-action-group">
                <button
                  className="status-btn status-btn-approve"
                  type="button"
                  onClick={() => saveStatus("approved")}
                  disabled={saving || status === "approved"}
                >
                  <i className="fas fa-check-circle" /> Approve
                </button>
                <button
                  className="status-btn status-btn-reject"
                  type="button"
                  onClick={() => saveStatus("rejected")}
                  disabled={saving || status === "rejected"}
                >
                  <i className="fas fa-times-circle" /> Reject
                </button>
              </div>
              <div className="status-action-group">
                <button
                  className="status-btn status-btn-progress"
                  type="button"
                  onClick={() => saveStatus("in_progress")}
                  disabled={saving || status === "in_progress"}
                >
                  <i className="fas fa-play-circle" /> Start Onboarding
                </button>
                <button
                  className="status-btn status-btn-live"
                  type="button"
                  onClick={() => saveStatus("live")}
                  disabled={saving || status === "live"}
                >
                  <i className="fas fa-rocket" /> Launch Website
                </button>
              </div>
              <div className="status-action-group">
                <button
                  className="website-status-review-btn"
                  type="button"
                  onClick={() => saveStatus("ready_for_review")}
                  disabled={saving || status === "ready_for_review"}
                >
                  <i className="fas fa-clipboard-check" /> Ready for Review
                </button>
                <button
                  className="status-btn status-btn-suspend"
                  type="button"
                  onClick={() => saveStatus("suspended")}
                  disabled={saving || status === "suspended"}
                >
                  <i className="fas fa-pause-circle" /> Suspend
                </button>
              </div>
            </div>
          </section>

          <section className="detail-card">
            <h3>
              <i className="fas fa-credit-card" /> Payment
            </h3>
            {request.LatestPaymentID ? (
              <>
                <p>
                  <strong>Status:</strong> {formatLabel(request.PaymentStatus)}
                </p>
                <p>
                  <strong>Amount:</strong>{" "}
                  {request.LatestPaymentCurrency || currency}{" "}
                  {Number(request.LatestPaymentAmount || 0).toFixed(2)}
                </p>
                <p>
                  <strong>Method:</strong>{" "}
                  {request.LatestPaymentMethod || "N/A"}
                </p>
                <p>
                  <strong>Reference:</strong>{" "}
                  {request.LatestPaymentReference || "N/A"}
                </p>
                {paymentDetails.instructions && (
                  <p>
                    <strong>Instructions:</strong> {paymentDetails.instructions}
                  </p>
                )}
                {request.PaymentStatus === "pending" && (
                  <button
                    className="status-btn status-btn-approve"
                    type="button"
                    onClick={confirmPayment}
                    disabled={confirmingPayment}
                  >
                    <i className="fas fa-check-circle" />{" "}
                    {confirmingPayment ? "Confirming..." : "Confirm payment"}
                  </button>
                )}
              </>
            ) : (
              <p>No payment has been recorded for this request.</p>
            )}
          </section>

          <section className="detail-card client-info-card">
            <h3>
              <i className="fas fa-user" /> Client Information
            </h3>
            <div className="client-info-grid">
              {[
                ["Full Name", request.AccountName],
                ["Contact", request.ContactName],
                ["Email", request.Email || request.AccountEmail],
                ["Phone", request.Phone],
                ["Business", request.BusinessName || request.ClientBusinessName],
                ["Package", request.PackageName],
                ["Domain", selectedDomain],
                ["Submitted", formatDate(request.CreatedAt)],
              ].map(([label, value]) => (
                <div className="info-item" key={label}>
                  <span className="info-label">{label}</span>
                  <span className="info-value">{value || "N/A"}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="detail-card">
            <h3>
              <i className="fas fa-laptop-code" /> Website Project
            </h3>
            <div className="client-info-grid">
              {[
                ["Existing website", request.ExistingWebsite],
                ["Project description", request.ProjectDescription],
              ].map(([label, value]) => (
                <div className="info-item" key={label}>
                  <span className="info-label">{label}</span>
                  <span className="info-value">{formatValue(value)}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="detail-card assessment-card website-readiness-card">
            <h3>
              <i className="fas fa-lightbulb" /> Business Readiness
            </h3>
            <p className="assessment-intro">
              A practical planning aid based on the client’s website project
              answers. It helps identify preparation and support needs; it is
              not a prediction of project success.
            </p>

            <section
              className="assessment-analysis"
              aria-label="Website business readiness analysis"
            >
              <div className="assessment-summary-grid">
                <div className="assessment-summary-item assessment-score">
                  <span className="assessment-summary-label">
                    Readiness index
                  </span>
                  <strong>
                    {readiness.score === null
                      ? "—"
                      : `${readiness.score}/100`}
                  </strong>
                  <span>{readiness.level}</span>
                </div>
                <div className="assessment-summary-item">
                  <span className="assessment-summary-label">
                    Support recommendation
                  </span>
                  <strong>{readiness.supportLevel || "Not assessed"}</strong>
                  <span>
                    {readiness.supportFactors.length
                      ? readiness.supportFactors.join(" · ")
                      : "No clear support flags"}
                  </span>
                </div>
                <div className="assessment-summary-item">
                  <span className="assessment-summary-label">
                    Scoring coverage
                  </span>
                  <strong>{readiness.coverage}%</strong>
                  <span>of weighted website planning areas answered</span>
                </div>
              </div>

              <p className="assessment-algorithm-note">
                Readiness uses the website purpose (20%), website type (15%),
                required pages and features (15%), content readiness (35%), and
                target launch date (15%). Missing or unrecognized answers are
                excluded and the score is recalculated from available answers.
              </p>

              {readiness.recommendations.length > 0 && (
                <div className="assessment-recommendations">
                  <h4>Suggested next actions</h4>
                  <ul>
                    {readiness.recommendations.map((recommendation) => (
                      <li key={recommendation}>{recommendation}</li>
                    ))}
                  </ul>
                </div>
              )}
            </section>

            {questionsError && (
              <p className="website-questionnaire-warning" role="status">
                {questionsError}
              </p>
            )}
            {answeredQuestions.length || unconfiguredAnswers.length ? (
              <dl className="assessment-answer-list website-readiness-answers">
                {answeredQuestions.map((question) => (
                  <div className="assessment-answer" key={question.id}>
                    <dt>{question.label}</dt>
                    <dd>{formatValue(answers[question.id])}</dd>
                  </div>
                ))}
                {unconfiguredAnswers.map(([questionId, answer]) => (
                  <div className="assessment-answer" key={questionId}>
                    <dt>
                      {questionId
                        .replace(/([a-z])([A-Z])/g, "$1 $2")
                        .replace(/[_-]/g, " ")
                        .replace(/^./, (letter) => letter.toUpperCase())}
                    </dt>
                    <dd>{formatValue(answer)}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="website-questionnaire-empty">
                No project-question answers were submitted.
              </p>
            )}
          </section>

          <section className="detail-card progress-card website-onboarding-progress-card">
            <h3>
              <i className="fas fa-tasks" /> Onboarding Progress
            </h3>
            <p className="website-onboarding-progress-intro">
              Progress reflects the configured website onboarding steps and
              information submitted with this request.
            </p>
            {stepsError && (
              <p className="website-questionnaire-warning" role="alert">
                {stepsError}
              </p>
            )}
            {progressSteps.length ? (
              <>
                <div className="progress-container">
                  <div className="progress-bar-wrapper">
                    <div
                      className="progress-bar"
                      role="progressbar"
                      aria-label="Website onboarding progress"
                      aria-valuenow={progressPercent}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    >
                      <div
                        className="progress-fill"
                        style={{ width: `${progressPercent}%` }}
                      />
                    </div>
                    <span className="progress-text">
                      {completedSteps}/{progressSteps.length} steps completed
                    </span>
                  </div>
                </div>
                <div className="steps-grid">
                  {progressSteps.map((step) => (
                    <div
                      key={step.id}
                      className={`step-item ${step.completed ? "completed" : ""}`}
                    >
                      <span
                        className="step-icon"
                        style={{ backgroundColor: step.color || "#4f46e5" }}
                        aria-hidden="true"
                      >
                        <i className={`fas ${step.icon || "fa-circle"}`} />
                      </span>
                      <span className="step-name">
                        Step {step.stepNumber}: {step.name}
                      </span>
                      <span className="step-status-label">
                        {step.completed ? "Submitted" : "Pending"}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="website-questionnaire-empty">
                No enabled website onboarding steps are configured.
              </p>
            )}
          </section>

          <section className="detail-card">
            <h3>
              <i className="fas fa-globe" /> Domain Selection
            </h3>
            <div className="client-info-grid">
              {[
                ["Domain option", formatLabel(domain.domainType)],
                ["Selected domain", selectedDomain],
                [
                  "Availability",
                  domain.availability?.available === true
                    ? "Available"
                    : domain.availability?.available === false
                      ? "Unavailable"
                      : "Not checked",
                ],
              ].map(([label, value]) => (
                <div className="info-item" key={label}>
                  <span className="info-label">{label}</span>
                  <span className="info-value">{value || "N/A"}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="detail-card document-card" id="website-request-invoice">
            <div className="document-card-header">
              <h3>
                <i className="fas fa-file-invoice-dollar" /> Invoice
              </h3>
              <button
                className="document-download-btn"
                type="button"
                title="Opens the print dialog; choose Save as PDF to download."
                onClick={() => printDocument("invoice")}
              >
                <i className="fas fa-download" /> Download PDF
              </button>
            </div>
            <p className="document-note">
              This invoice is generated from the invoice details saved with the
              website request.
            </p>
            <div className="print-document invoice-document">
              <div className="document-title-row">
                <div>
                  <h2>Invoice</h2>
                  <p>Toora Flex (Pty) Ltd</p>
                </div>
                <div className="invoice-number">
                  <span>Invoice number</span>
                  <strong>
                    {invoice.invoiceNumber || request.InvoiceNumber || "N/A"}
                  </strong>
                </div>
              </div>
              <div className="invoice-meta">
                <p>
                  <strong>Bill to:</strong> {request.BusinessName || "N/A"}
                </p>
                <p>
                  <strong>Client:</strong>{" "}
                  {request.ContactName || request.AccountName || "N/A"}
                </p>
                <p>
                  <strong>Email:</strong>{" "}
                  {request.Email || request.AccountEmail || "N/A"}
                </p>
                <p>
                  <strong>Date:</strong> {formatDate(request.CreatedAt)}
                </p>
              </div>
              <table className="invoice-lines">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{request.PackageName || "Website package"}</td>
                    <td>
                      {invoice.packageAmount == null
                        ? "Custom quote"
                        : `${currency} ${Number(invoice.packageAmount).toFixed(2)}`}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      Annual domain charge
                      {selectedDomain !== "N/A" ? ` (${selectedDomain})` : ""}
                    </td>
                    <td>
                      {invoice.domainAmount == null
                        ? `${currency} 0.00`
                        : `${currency} ${Number(invoice.domainAmount).toFixed(2)}`}
                    </td>
                  </tr>
                  <tr>
                    <td>Domain tax</td>
                    <td>
                      {invoice.domainTax == null
                        ? `${currency} 0.00`
                        : `${currency} ${Number(invoice.domainTax).toFixed(2)}`}
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr>
                    <th>Total due</th>
                    <th>{totalDue}</th>
                  </tr>
                </tfoot>
              </table>
              <p className="invoice-payment-method">
                Payment status: {formatLabel(request.PaymentStatus)}
              </p>
              {request.LatestPaymentMethod && (
                <p className="invoice-payment-method">
                  Payment method: {request.LatestPaymentMethod}
                </p>
              )}
            </div>
          </section>

          <section className="detail-card document-card">
            <div className="document-card-header">
              <h3>
                <i className="fas fa-file-contract" /> Service Agreement
              </h3>
              <button
                className="document-download-btn"
                type="button"
                title="Opens the print dialog; choose Save as PDF to download."
                onClick={() => printDocument("contract")}
              >
                <i className="fas fa-download" /> Download PDF
              </button>
            </div>
            <div className="print-document contract-document">
              <h2>{request.ContractName || "Website Service Agreement"}</h2>
              <pre>{request.ContractContent || "Agreement content unavailable."}</pre>
              <p>
                <strong>Accepted:</strong>{" "}
                {request.ContractAcceptedAt
                  ? formatDate(request.ContractAcceptedAt)
                  : "Not recorded"}
              </p>
            </div>
          </section>
        </div>

        <aside className="detail-right-column">
          <section className="detail-card website-request-manage-card">
            <h3>
              <i className="fas fa-sliders-h" /> Manage Website Request
            </h3>
            {isQuoteRequired ? (
              <form className="website-request-quote-form" onSubmit={saveQuote}>
                <label htmlFor="website-request-quote">
                  Set custom quote ({currency})
                </label>
                <input
                  id="website-request-quote"
                  type="number"
                  min="0.01"
                  step="0.01"
                  required
                  value={quote}
                  onChange={(event) => setQuote(event.target.value)}
                  placeholder="Enter quote amount"
                />
                <button
                  className="website-request-primary"
                  type="submit"
                  disabled={saving || !quote}
                >
                  {saving ? "Saving..." : "Save quote"}
                </button>
              </form>
            ) : (
              <p className="website-request-quote-status">
                {invoice.totalDue == null
                  ? "No custom quote is required."
                  : `Invoice total: ${totalDue}`}
              </p>
            )}
          </section>
          <section className="website-request-meta">
            <strong>Request details</strong>
            <span>Last updated</span>
            <code>{formatDate(request.UpdatedAt)}</code>
            <span>Request ID</span>
            <code>{request.WebsiteRequestID}</code>
            <span>Invoice number</span>
            <code>{request.InvoiceNumber || "Not issued"}</code>
          </section>
        </aside>
      </div>
    </div>
  );
};

export default WebsiteRequestDetail;
